import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, ArrowRight, PackageCheck, ShoppingCart, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inventoryApi } from "./inventoryApi";
import { REQUEST_STATUSES, REQUEST_NEXT, PRIORITY_MAP } from "./procurementConstants";
import { formatDateTime } from "./inventoryConstants";

export const RequestDetailModal = ({ requestId, onClose, onChanged, canManage, vendors, currentUser }) => {
  const [req, setReq] = useState(null);
  const [poVendor, setPoVendor] = useState("");
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";

  const load = () => { if (requestId) inventoryApi.getRequest(requestId).then(setReq).catch(() => {}); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [requestId]);

  if (!requestId || !req) return null;
  const status = REQUEST_STATUSES[req.status] || {};
  const pr = PRIORITY_MAP[req.priority] || {};
  const nexts = REQUEST_NEXT[req.status] || [];

  const changeStatus = async (s) => {
    try { await inventoryApi.updateRequestStatus(req.id, { status: s, user: userName }); toast.success(`Marked ${REQUEST_STATUSES[s]?.label || s}`); load(); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const issueLine = async (line) => {
    if (!line.part_id) { toast.error("Only inventory-linked lines can be issued"); return; }
    try {
      await inventoryApi.useParts({
        part_id: line.part_id, unit_id: req.unit_id, unit_number: req.unit_number, unit_type: req.unit_type,
        quantity: line.quantity, source: "inventory_stock", request_id: req.id, installed_by: userName,
        description: line.description,
      });
      toast.success("Issued to unit");
      load(); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to issue"); }
  };

  const createPO = async () => {
    if (!poVendor) { toast.error("Select a vendor"); return; }
    try { const po = await inventoryApi.createPoFromRequest(req.id, { vendor_id: poVendor, created_by: userName }); toast.success(`Created ${po.po_number}`); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to create PO"); }
  };

  const canIssue = ["approved", "reserved", "part_received"].includes(req.status);

  return (
    <Dialog open={!!requestId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0 h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col" hideClose data-testid="request-detail-modal">
        <DialogTitle className="sr-only">{req.request_number}</DialogTitle>
        <DialogDescription className="sr-only">Parts request details and workflow</DialogDescription>
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-900">{req.request_number}</h2>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.bg} ${status.color}`}>{status.label}</span>
            </div>
            <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${pr.bg} ${pr.color}`}>{pr.label}</span>
              {req.unit_number && <span>Unit {req.unit_number}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {req.description && <div><div className="text-xs text-slate-500">Issue</div><p className="text-sm text-slate-800">{req.description}</p></div>}

          <div>
            <div className="text-xs text-slate-500 mb-2">Line Items</div>
            <div className="space-y-2">
              {req.line_items.map((l) => (
                <div key={l.id} className="flex items-center justify-between gap-2 border border-slate-100 rounded-lg p-3" data-testid="request-line-item">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{l.description}</div>
                    <div className="text-xs text-slate-500">{l.part_number ? `${l.part_number} · ` : ""}Qty {l.quantity}{l.core_required ? " · Core" : ""}</div>
                    <div className="flex gap-2 mt-1">
                      {l.qty_reserved > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-700">Reserved {l.qty_reserved}</span>}
                      {l.qty_issued > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700">Issued {l.qty_issued}</span>}
                    </div>
                  </div>
                  {canManage && canIssue && l.part_id && (l.qty_issued || 0) < l.quantity && (
                    <Button size="sm" variant="outline" onClick={() => issueLine(l)} data-testid="issue-line-btn"><Wrench className="w-3.5 h-3.5 mr-1" /> Issue</Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {req.notes && <div><div className="text-xs text-slate-500">Notes</div><p className="text-sm text-slate-700">{req.notes}</p></div>}

          <div>
            <div className="text-xs text-slate-500 mb-2">History</div>
            <div className="space-y-1">
              {(req.status_history || []).map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-slate-500">
                  <PackageCheck className="w-3 h-3" /> {REQUEST_STATUSES[h.status]?.label || h.status} · {h.user || "—"} · {formatDateTime(h.timestamp)}
                </div>
              ))}
            </div>
          </div>
        </div>

        {canManage && (
          <div className="px-5 py-4 border-t border-slate-200 flex-shrink-0 space-y-3">
            {["need_to_purchase", "approved", "reserved", "under_review", "submitted"].includes(req.status) && (
              <div className="flex gap-2">
                <select className="flex-1 h-9 px-2 rounded-md border border-slate-200 text-sm" value={poVendor} onChange={(e) => setPoVendor(e.target.value)} data-testid="po-vendor-select">
                  <option value="">Select vendor for PO...</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                <Button variant="outline" onClick={createPO} data-testid="create-po-btn"><ShoppingCart className="w-4 h-4 mr-1" /> Create PO</Button>
              </div>
            )}
            {nexts.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {nexts.map((s) => (
                  <Button key={s} size="sm" variant={["denied", "cancelled"].includes(s) ? "outline" : "default"} onClick={() => changeStatus(s)} data-testid={`status-${s}-btn`}>
                    {REQUEST_STATUSES[s]?.label || s} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
